import { useState, useEffect, useRef, useMemo } from 'react'
import { User, Phone, Mail, CheckCircle2, Shield } from 'lucide-react'
import { useUser } from '../../../../context/UserContext'
import {
  signInWithPhoneNumber,
  ConfirmationResult,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { requireAuth } from '../../../../lib/firebase'
import {
  clearPhoneRecaptcha,
  formatFirebaseOtpError,
  initPhoneRecaptcha,
  isFirebasePhoneConfigured,
} from '../../../../lib/phoneRecaptcha'

interface PersonalInfoStepProps {
  fullName: string
  phone: string
  email: string
  errors: { [key: string]: string }
  onChange: (name: string, value: string) => void
  /** Bubbles up phone verification status so the wizard can gate the Next button. */
  onPhoneVerifiedChange?: (verified: boolean) => void
  /** Bubbles up email verification status (required when logged-in user changes email). */
  onEmailVerifiedChange?: (verified: boolean) => void
}

declare global {
  interface Window {
    recaptchaVerifierPhone: RecaptchaVerifier | null
  }
}

const formatPhoneForDisplay = (value: string) =>
  value.replace(/^\+91/, '').replace(/\D/g, '').slice(0, 10)

const readStoredAccountPhone = () => {
  try {
    const raw = localStorage.getItem('firebase_auth_user')
    if (!raw) return ''
    const data = JSON.parse(raw)
    return formatPhoneForDisplay(data.phoneNumber || data.phone_number || '')
  } catch {
    return ''
  }
}

const readStoredAccountEmail = () => {
  try {
    const raw = localStorage.getItem('firebase_auth_user')
    if (!raw) return ''
    const data = JSON.parse(raw)
    return String(data.email || '').trim().toLowerCase()
  } catch {
    return ''
  }
}

const PersonalInfoStep = ({
  fullName,
  phone,
  email,
  errors,
  onChange,
  onPhoneVerifiedChange,
  onEmailVerifiedChange,
}: PersonalInfoStepProps) => {
  const { userDetails, setUser, updatePhoneNumber } = useUser()
  const isLoggedIn = !!userDetails
  const hasPrefilledName = isLoggedIn && !!userDetails.displayName
  const hasPrefilledEmail = isLoggedIn && !!userDetails.email
  const hasPrefilledPhone = isLoggedIn && !!userDetails.phoneNumber
  const isPreFilled = hasPrefilledName || hasPrefilledEmail || hasPrefilledPhone

  const [verifiedPhoneDigits, setVerifiedPhoneDigits] = useState(readStoredAccountPhone)
  const [verifiedEmail, setVerifiedEmail] = useState(readStoredAccountEmail)
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false)
  const [emailVerifyError, setEmailVerifyError] = useState<string | null>(null)

  const currentPhoneDigits = useMemo(
    () => formatPhoneForDisplay(phone),
    [phone]
  )
  const emailValid = useMemo(() => /\S+@\S+\.\S+/.test(email || ''), [email])
  const normalizedEmail = email.trim().toLowerCase()
  const phoneMatchesVerified =
    currentPhoneDigits.length === 10 && currentPhoneDigits === verifiedPhoneDigits
  const emailMatchesVerified =
    emailValid && (!isLoggedIn || (normalizedEmail !== '' && normalizedEmail === verifiedEmail))
  const needsPhoneVerification =
    currentPhoneDigits.length === 10 && !phoneMatchesVerified
  const needsEmailVerification = isLoggedIn && emailValid && !emailMatchesVerified

  useEffect(() => {
    if (!userDetails) return
    const accountPhone = formatPhoneForDisplay(userDetails.phoneNumber || '')
    if (accountPhone.length === 10) {
      setVerifiedPhoneDigits(accountPhone)
    }
    if (userDetails.email) {
      setVerifiedEmail(userDetails.email.trim().toLowerCase())
    }
  }, [userDetails?.phoneNumber, userDetails?.email])

  useEffect(() => {
    onPhoneVerifiedChange?.(phoneMatchesVerified)
  }, [phoneMatchesVerified, onPhoneVerifiedChange])

  useEffect(() => {
    onEmailVerifiedChange?.(emailMatchesVerified)
  }, [emailMatchesVerified, onEmailVerifiedChange])

  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otp, setOtp] = useState('')
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null)
  const [resendTimer, setResendTimer] = useState(0)
  const [isVerifying, setIsVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [recaptchaKey, setRecaptchaKey] = useState(0)
  const [recaptchaReady, setRecaptchaReady] = useState(false)
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendTimer])

  useEffect(() => {
    if (!userDetails) return

    if (hasPrefilledName && !fullName.trim()) {
      onChange('fullName', userDetails.displayName || '')
    }

    if (hasPrefilledEmail && !email.trim()) {
      onChange('email', userDetails.email || '')
    }

    if (hasPrefilledPhone && !formatPhoneForDisplay(phone).trim()) {
      onChange('phone', formatPhoneForDisplay(userDetails.phoneNumber || ''))
    }
  }, [
    userDetails,
    hasPrefilledName,
    hasPrefilledEmail,
    hasPrefilledPhone,
    fullName,
    email,
    phone,
    onChange
  ])

  useEffect(() => {
    if (!showPhoneVerification) return

    let cancelled = false

    const initializeRecaptcha = async () => {
      setRecaptchaReady(false)

      if (!isFirebasePhoneConfigured()) {
        setVerifyError('Phone verification is not configured. Please contact support.')
        return
      }

      try {
        clearPhoneRecaptcha(window.recaptchaVerifierPhone)
        window.recaptchaVerifierPhone = null

        window.recaptchaVerifierPhone = await initPhoneRecaptcha('recaptcha-container-phone', {
          onExpired: () => setRecaptchaKey((prevKey) => prevKey + 1),
          onError: () => setVerifyError('Security verification failed. Please try again.'),
        })

        if (!cancelled) {
          setRecaptchaReady(true)
        }
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error)
        if (!cancelled) {
          setVerifyError('Failed to initialize security verification. Please refresh and try again.')
        }
      }
    }

    initializeRecaptcha()

    return () => {
      cancelled = true
      clearPhoneRecaptcha(window.recaptchaVerifierPhone)
      window.recaptchaVerifierPhone = null
      setRecaptchaReady(false)
    }
  }, [recaptchaKey, showPhoneVerification])

  const handleSendOTP = async () => {
    const digits = formatPhoneForDisplay(phoneNumber)
    if (!digits || digits.length !== 10) {
      setVerifyError('Please enter a valid 10-digit phone number')
      return
    }

    setIsVerifying(true)
    setVerifyError(null)

    try {
      if (!window.recaptchaVerifierPhone || !recaptchaReady) {
        throw new Error('Security verification is still loading. Please wait a moment and try again.')
      }

      const appVerifier = window.recaptchaVerifierPhone
      const formattedPhone = `+91${digits}`

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout. Please try again.')), 45000)
      )

      const result = await Promise.race([
        signInWithPhoneNumber(requireAuth(), formattedPhone, appVerifier),
        timeoutPromise,
      ]) as ConfirmationResult

      setConfirmationResult(result)
      setResendTimer(30)
      setIsVerifying(false)

      setTimeout(() => {
        otpInputRefs.current[0]?.focus()
      }, 100)
    } catch (err: unknown) {
      console.error('Error sending OTP:', err)
      setVerifyError(formatFirebaseOtpError(err))
      setRecaptchaKey((prevKey) => prevKey + 1)
      setIsVerifying(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return
    const newOtp = otp.split('')
    newOtp[index] = value
    const updatedOtp = newOtp.join('')
    setOtp(updatedOtp)

    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/\D/g, '')
    if (pastedData.length === 6) {
      setOtp(pastedData)
      otpInputRefs.current[5]?.focus()
    }
  }

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      setVerifyError('Please enter a valid 6-digit OTP')
      return
    }

    setIsVerifying(true)
    setVerifyError(null)

    try {
      if (!confirmationResult) {
        throw new Error('Verification session expired. Please request a new OTP.')
      }

      const result = await confirmationResult.confirm(otp)
      if (!result?.user) {
        throw new Error('Authentication failed. Please try again.')
      }

      const firebaseIdToken = await result.user.getIdToken()
      localStorage.setItem('firebase_auth_idToken', firebaseIdToken)

      const verifiedPhone = result.user.phoneNumber || `+91${formatPhoneForDisplay(phoneNumber)}`
      if (userDetails?.email) {
        // Logged-in Google user: OTP proves ownership — update phone only, keep profile data
        updatePhoneNumber(verifiedPhone)
      } else {
        setUser(result.user)
      }

      // Update phone in form
      const digits = formatPhoneForDisplay(phoneNumber)
      onChange('phone', digits)
      setVerifiedPhoneDigits(digits)

      // Close verification UI
      setShowPhoneVerification(false)
      setOtp('')
      setPhoneNumber('')
      setConfirmationResult(null)
      setIsVerifying(false)
    } catch (err: any) {
      console.error('Error verifying OTP:', err)
      if (err?.code === 'auth/invalid-verification-code') {
        setVerifyError('Invalid OTP. Please check and try again.')
      } else if (err?.code === 'auth/code-expired') {
        setVerifyError('OTP has expired. Please request a new one.')
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setVerifyError('This phone number is linked to another account. Please use a different number or sign in with that account.')
      } else {
        setVerifyError(err?.message || 'Verification failed. Please try again.')
      }
      setIsVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendTimer > 0) return

    setIsVerifying(true)
    setVerifyError(null)

    try {
      clearPhoneRecaptcha(window.recaptchaVerifierPhone)
      window.recaptchaVerifierPhone = await initPhoneRecaptcha('recaptcha-container-phone', {
        onExpired: () => setRecaptchaKey((prevKey) => prevKey + 1),
        onError: () => setVerifyError('Security verification failed. Please try again.'),
      })
      setRecaptchaReady(true)

      const appVerifier = window.recaptchaVerifierPhone
      const digits = formatPhoneForDisplay(phoneNumber)
      const formattedPhone = `+91${digits}`
      const result = await signInWithPhoneNumber(requireAuth(), formattedPhone, appVerifier)

      setConfirmationResult(result)
      setResendTimer(30)
      setOtp('')
      setIsVerifying(false)

      setTimeout(() => {
        otpInputRefs.current[0]?.focus()
      }, 100)
    } catch (err: unknown) {
      console.error('Error resending OTP:', err)
      const e = err as { code?: string }
      if (e?.code === 'auth/too-many-requests') {
        setVerifyError('Too many attempts. Please wait a few minutes before trying again.')
        setResendTimer(180)
      } else {
        setVerifyError(formatFirebaseOtpError(err))
        setRecaptchaKey((prevKey) => prevKey + 1)
      }
      setIsVerifying(false)
    }
  }

  const handleVerifyEmail = async () => {
    if (!emailValid) {
      setEmailVerifyError('Enter a valid email address first')
      return
    }

    setIsVerifyingEmail(true)
    setEmailVerifyError(null)

    try {
      const provider = new GoogleAuthProvider()
      provider.addScope('profile')
      provider.addScope('email')
      provider.setCustomParameters({ login_hint: email })

      const result = await signInWithPopup(requireAuth(), provider)
      const googleEmail = result.user.email?.trim().toLowerCase()

      if (!googleEmail) {
        throw new Error('Could not read email from Google account')
      }

      if (googleEmail !== normalizedEmail) {
        setEmailVerifyError(
          `Google signed in as ${googleEmail}. Use that email or sign in with the matching account.`
        )
        return
      }

      const firebaseIdToken = await result.user.getIdToken()
      localStorage.setItem('firebase_auth_idToken', firebaseIdToken)
      setUser(result.user)
      setVerifiedEmail(googleEmail)
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string }
      if (e?.code === 'auth/popup-closed-by-user') {
        const currentUser = requireAuth().currentUser
        if (currentUser?.email?.trim().toLowerCase() === normalizedEmail) {
          setVerifiedEmail(normalizedEmail)
          return
        }
        setEmailVerifyError('Sign-in was cancelled. Verify email to continue.')
        return
      }
      setEmailVerifyError(e?.message || 'Email verification failed. Please try again.')
    } finally {
      setIsVerifyingEmail(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h5 className="text-sm sm:text-base text-gray-900">
          <span className="font-bold">Personal Information</span>
          <span className="text-gray-600 font-normal"> — {isPreFilled
            ? 'Your details have been pre-filled from your account'
            : 'Please provide your contact details'}</span>
        </h5>
      </div>
      {/* <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Personal Information</h3>
        <p className="text-xs sm:text-sm text-gray-600">
          {isPreFilled
            ? 'Your details have been pre-filled from your account'
            : 'Please provide your contact details'}
        </p>
        {isPreFilled && (
          <div className="mt-2 flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            <CheckCircle2 className="h-4 w-4" />
            <span>Verified account details</span>
          </div>
        )}
      </div> */}

      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-2 min-w-0">
            <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
              <User className="h-4 w-4 text-primary-500 shrink-0" />
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              value={fullName}
              onChange={(e) => onChange('fullName', e.target.value)}
              placeholder="Enter your full name"
              className={`w-full px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-xl focus:ring-4 focus:ring-primary-100 text-gray-900 transition-all duration-200 text-base sm:text-lg ${errors.fullName
                ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                : 'border-gray-200 focus:border-primary-500'
                }`}
              required
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
            )}
          </div>

          <div className="space-y-2 min-w-0">
            <label className="block text-sm font-semibold text-gray-800 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="inline-flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary-500 shrink-0" />
                Email Address *
              </span>
              {hasPrefilledEmail && emailMatchesVerified && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                  <CheckCircle2 className="h-3 w-3" />
                  Verified
                </span>
              )}
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                onChange('email', e.target.value)
                setEmailVerifyError(null)
              }}
              placeholder="Enter your email address"
              className={`w-full px-3 sm:px-4 py-3 sm:py-4 border-2 rounded-xl focus:ring-4 focus:ring-primary-100 text-gray-900 transition-all duration-200 text-base sm:text-lg ${errors.email
                ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                : emailMatchesVerified
                  ? 'border-green-300 bg-green-50/40'
                  : 'border-gray-200 focus:border-primary-500'
                }`}
              required
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            {needsEmailVerification && (
              <div className="space-y-2">
                <p className="text-xs text-amber-700">
                  You changed your email — verify with Google to continue.
                </p>
                <button
                  type="button"
                  onClick={handleVerifyEmail}
                  disabled={isVerifyingEmail}
                  className="w-full px-4 py-2.5 bg-white border-2 border-primary-200 text-primary-700 rounded-xl font-semibold text-sm hover:bg-primary-50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isVerifyingEmail ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4" />
                      <span>Verify email with Google</span>
                    </>
                  )}
                </button>
                {emailVerifyError && <p className="text-red-500 text-xs">{emailVerifyError}</p>}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary-500" />
            Phone Number *
            {phoneMatchesVerified && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                <CheckCircle2 className="h-3 w-3" />
                Verified
              </span>
            )}
          </label>
          <div className="relative">
            <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-600 font-medium text-base sm:text-lg">
              +91
            </div>
            <input
              type="tel"
              name="phone"
              value={formatPhoneForDisplay(phone)}
              onChange={(e) => {
                const formatted = formatPhoneForDisplay(e.target.value)
                onChange('phone', formatted)
                if (showPhoneVerification) {
                  setShowPhoneVerification(false)
                  setOtp('')
                  setConfirmationResult(null)
                  setVerifyError(null)
                }
              }}
              placeholder="Enter 10-digit phone number"
              maxLength={10}
              inputMode="numeric"
              className={`w-full pl-12 sm:pl-14 pr-3 sm:pr-4 py-3 sm:py-4 border-2 rounded-xl focus:ring-4 focus:ring-primary-100 text-gray-900 transition-all duration-200 text-base sm:text-lg ${errors.phone
                ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                : phoneMatchesVerified
                  ? 'border-green-300 bg-green-50/40'
                  : 'border-gray-200 focus:border-primary-500'
                }`}
              required
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
          )}

          {/* Phone Verification UI — shown whenever number differs from last verified */}
          {needsPhoneVerification && (
            <>
              {!showPhoneVerification && (
                <button
                  type="button"
                  onClick={() => {
                    // Pre-fill phone number from main field if available
                    if (phone && formatPhoneForDisplay(phone).length === 10) {
                      setPhoneNumber(formatPhoneForDisplay(phone))
                    }
                    setShowPhoneVerification(true)
                  }}
                  className="w-full mt-2 px-4 py-2.5 bg-primary-50 border-2 border-primary-200 text-primary-700 rounded-xl font-semibold text-sm hover:bg-primary-100 hover:border-primary-300 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Shield className="h-4 w-4" />
                  <span>Verify Phone Number</span>
                </button>
              )}

              {showPhoneVerification && (
                <div className="mt-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <Shield className="h-4 w-4 text-primary-500" />
                      Verify Phone Number
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPhoneVerification(false)
                        setPhoneNumber('')
                        setOtp('')
                        setVerifyError(null)
                        setConfirmationResult(null)
                      }}
                      className="text-gray-400 hover:text-gray-600 text-sm"
                    >
                      Cancel
                    </button>
                  </div>

                  {!confirmationResult ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600 font-medium">
                          +91
                        </div>
                        <input
                          type="tel"
                          value={formatPhoneForDisplay(phoneNumber)}
                          onChange={(e) => {
                            const formatted = formatPhoneForDisplay(e.target.value)
                            setPhoneNumber(formatted)
                          }}
                          placeholder="Enter 10-digit phone number"
                          disabled={isVerifying}
                          maxLength={10}
                          inputMode="numeric"
                          className="w-full pl-12 pr-3 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 text-base disabled:bg-gray-50"
                        />
                      </div>
                      {verifyError && (
                        <p className="text-red-500 text-xs">{verifyError}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={isVerifying || !recaptchaReady || formatPhoneForDisplay(phoneNumber).length !== 10}
                        className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isVerifying ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Sending OTP...</span>
                          </>
                        ) : !recaptchaReady ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Preparing verification...</span>
                          </>
                        ) : (
                          'Send OTP'
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-2">
                          Enter 6-digit OTP sent to +91{formatPhoneForDisplay(phoneNumber)}
                        </label>
                        <div className="flex justify-center gap-2">
                          {[0, 1, 2, 3, 4, 5].map((index) => (
                            <input
                              key={index}
                              ref={(el) => (otpInputRefs.current[index] = el)}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={otp[index] || ''}
                              onChange={(e) => handleOtpChange(index, e.target.value)}
                              onKeyDown={(e) => handleOtpKeyDown(index, e)}
                              onPaste={handleOtpPaste}
                              disabled={isVerifying}
                              className="w-10 h-10 text-center text-lg font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 disabled:bg-gray-50"
                            />
                          ))}
                        </div>
                      </div>
                      {verifyError && (
                        <p className="text-red-500 text-xs">{verifyError}</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleVerifyOTP}
                          disabled={isVerifying || otp.length !== 6}
                          className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isVerifying ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4" />
                              <span>Verify</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="text-center">
                        {resendTimer > 0 ? (
                          <p className="text-xs text-gray-500">
                            Resend OTP in <span className="font-semibold">{resendTimer}s</span>
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={isVerifying}
                            className="text-primary-500 hover:text-primary-600 font-semibold text-xs transition-colors disabled:opacity-50"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>


      </div>

      {/* reCAPTCHA Container */}
      <div id="recaptcha-container-phone" key={recaptchaKey} style={{ visibility: 'hidden', position: 'absolute' }}></div>
    </div>
  )
}

export default PersonalInfoStep

